import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, FadeIn, MaskedLines } from "@/components/Motion";
import { CEO, IMAGES, EXPERTISE_AREAS, CREDENTIALS, AWARDS, CORE_VALUES, COMMITTEES, INDUSTRIES, TECH_CAPABILITIES, TECH_IMAGES } from "@/data/site";

function PageHero() {
  return (
    <section className="relative overflow-hidden bg-navy-deep pb-20 pt-40 lg:pb-28 lg:pt-52">
      <img
        src={IMAGES.aboutOverview}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.14]"
        loading="eager"
        fetchPriority="high"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 to-navy-deep"
        aria-hidden="true"
      />
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="container-x relative max-w-3xl">
        <FadeIn y={16}>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            About Us
          </p>
        </FadeIn>
        <h1 className="mt-7 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
          <MaskedLines
            delay={0.3}
            lines={[
              <span key="a1">Shaping the Future</span>,
              <span key="a2">
                of <em className="font-medium italic text-gold">Enterprise</em>
              </span>,
            ]}
          />
        </h1>
        <FadeIn delay={0.9}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            Strategic thinking, financial expertise, and operational insight to
            help organizations build stronger foundations for sustainable growth.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function Overview() {
  return (
    <section className="bg-white py-20 lg:py-32" data-testid="about-overview">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Who We Are
          </p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
            An Established Nigerian Professional Services Firm
          </h2>
          <span aria-hidden="true" className="mt-8 block h-px w-20 bg-gold" />
          <p className="mt-8 text-lg leading-relaxed text-charcoal/80">
            Established in 2002, Steven Philips &amp; Associates Limited (SPA)
            is a Nigerian professional services firm specializing in business
            and management consultancy, training, software development, and
            enterprise software implementation.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/70">
            The company serves small, medium, and large organizations across
            Nigeria and the wider West African market — supporting institutions
            seeking to modernize operations, improve financial management,
            integrate business systems, and enhance organizational performance.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/70">
            Alongside its advisory practice, SPA provides consulting,
            development, implementation, customization, and training services
            for Sage and other business software solutions, and develops and
            hosts corporate enterprise websites and portals — including
            web-based applications and transactional solutions designed around
            client requirements.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <figure className="group relative">
            <span
              aria-hidden="true"
              className="absolute -left-4 -top-4 hidden h-full w-full border border-gold/40 sm:block"
            />
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.aboutOverview}
                alt="Skyline of Lagos, Nigeria, home of Steven Philips & Associates Limited"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

function TechCapabilities() {
  return (
    <section className="bg-mist py-20 lg:py-28" data-testid="about-tech">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Enterprise Technology
          </p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
            Enterprise Technology Capabilities
          </h2>
          <span aria-hidden="true" className="mt-8 block h-px w-20 bg-gold" />
          <p className="mt-8 text-lg leading-relaxed text-charcoal/80">
            For more than two decades — since 2002 — we have helped
            organizations select, implement, and master the business software
            that runs their operations, from ERP systems to business process
            automation.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/70">
            Our experience spans reselling and implementing Sage 300 ERP
            (formerly Sage Accpac ERP) alongside other Sage business software
            solutions — with consulting, development, implementation,
            customization, and training delivered end to end.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/70">
            SPA also develops and hosts corporate enterprise websites and
            portals, including web-based applications and transactional
            solutions designed around client requirements.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2">
            {TECH_CAPABILITIES.map((c) => (
              <div
                key={c}
                className="bg-mist p-6 transition-colors duration-500 hover:bg-white"
              >
                <span aria-hidden="true" className="block h-px w-8 bg-gold" />
                <p className="mt-4 text-[14px] font-semibold leading-snug text-navy">
                  {c}
                </p>
              </div>
            ))}
            <div className="hidden bg-mist sm:block" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="about-industries">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Sectors</p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
            Industries We Serve
          </h2>
          <span aria-hidden="true" className="mt-8 block h-px w-20 bg-gold" />
          <p className="mt-8 text-base leading-relaxed text-charcoal/70 sm:text-lg">
            Organizations across the public and private sectors rely on SPA for
            professional services and enterprise technology support.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {INDUSTRIES.map((ind, i) => (
            <div
              key={ind}
              className="bg-white p-7 transition-colors duration-500 hover:bg-mist"
            >
              <span className="font-serif text-base text-gold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="mt-3 font-serif text-lg font-semibold leading-snug text-navy">
                {ind}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Leadership() {
  return (
    <section
      id="leadership"
      className="scroll-mt-24 border-t border-navy/10 bg-white py-20 lg:py-32"
      data-testid="about-leadership"
    >
      <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div>
          <Reveal className="lg:sticky lg:top-28">
            <figure className="relative">
              <span
                aria-hidden="true"
                className="absolute -left-4 -top-4 h-full w-full border border-gold/50"
              />
              <div className="relative overflow-hidden">
                <img
                  src={CEO.photo}
                  alt="Stephen Irebia, Managing Director and CEO of Steven Philips & Associates Limited."
                  className="aspect-[11/14] w-full object-cover object-top"
                  loading="eager"
                />
              </div>
              <figcaption className="mt-6">
                <span className="block font-serif text-xl font-semibold text-navy">
                  {CEO.name}
                </span>
                <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                  {CEO.position}
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div>
          <Reveal>
            <p className="eyebrow flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-gold" />
              Leadership
            </p>
            <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
              Experienced Leadership. Enduring Values.
            </h2>
            <h3 className="mt-10 font-serif text-2xl font-semibold text-navy sm:text-3xl">
              {CEO.name}
            </h3>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
              {CEO.position}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-charcoal/75">
              <p>
                Stephen Irebia, FCA, CPA, is an experienced financial
                management professional and strategic business adviser with more
                than three decades of experience in audit, accounting, financial
                advisory, corporate governance, and organizational leadership.
              </p>
              <p>
                A Fellow of the Institute of Chartered Accountants of Nigeria
                (ICAN), he has built a professional reputation around financial
                discipline, sound governance, strategic insight, and ethical
                leadership.
              </p>
              <p>
                His professional experience encompasses corporate financial
                restructuring, risk management, financial oversight, tax strategy,
                business advisory, and turnaround initiatives.
              </p>
              <p>
                Beyond his professional engagements, he has contributed to
                public-sector financial oversight, policy discussions,
                professional mentorship, and community development.
              </p>
              <p>
                His leadership philosophy emphasizes integrity, accountability,
                excellence, and sustainable value creation.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h3 className="mt-14 font-serif text-2xl font-semibold text-navy">
              Professional Expertise
            </h3>
            <div className="mt-6 border-t border-navy/10">
              {EXPERTISE_AREAS.map((area, i) => (
                <div
                  key={area.title}
                  className="group grid grid-cols-[auto_1fr] gap-6 border-b border-navy/10 py-6 transition-colors duration-300 hover:bg-mist/60"
                >
                  <span className="font-serif text-base text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h4 className="font-serif text-lg font-semibold text-navy sm:text-xl">
                      {area.title}
                    </h4>
                    <p className="mt-2 text-[15px] leading-relaxed text-charcoal/65">
                      {area.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h3 className="mt-14 font-serif text-2xl font-semibold text-navy">
              Public Service and Committee Leadership
            </h3>
            <p className="mt-4 text-base leading-relaxed text-charcoal/70">
              His commitment to sound stewardship extends to public service,
              where he has brought financial discipline and independent oversight
              to committee work.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-3">
              {COMMITTEES.map((c) => (
                <li
                  key={c}
                  className="border-l-2 border-gold/60 bg-mist px-4 py-3 text-sm font-semibold text-navy"
                >
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <h3 className="mt-14 font-serif text-2xl font-semibold text-navy">
              Mentorship and Community Empowerment
            </h3>
            <p className="mt-4 text-base leading-relaxed text-charcoal/70">
              Beyond the boardroom, he is committed to developing the next
              generation of professionals and giving back to the community.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Mentorship of aspiring accountants",
                "Guidance on professional accounting examinations and career development",
                "Educational support initiatives",
                "Free JAMB registration assistance for students",
                "Community empowerment and philanthropic activities",
              ].map((item) => (
                <li key={item} className="flex items-start gap-4 text-[15px] text-charcoal/75">
                  <span aria-hidden="true" className="mt-[10px] h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Credentials() {
  return (
    <section className="bg-mist py-20 lg:py-28" data-testid="about-credentials">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Credentials</p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
            Professional Qualifications &amp; Recognition
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {CREDENTIALS.map((c, i) => (
            <Reveal key={c.abbr} delay={i * 0.1}>
              <div className="h-full border border-navy/10 border-t-2 border-t-gold bg-white p-10">
                <span className="font-serif text-5xl font-semibold text-gold">
                  {c.abbr}
                </span>
                <p className="mt-6 text-base leading-relaxed text-charcoal/80">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Honours() {
  return (
    <section className="bg-white py-20 lg:py-32" data-testid="about-honours">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Recognition</p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
            Honours &amp; Recognition
          </h2>
        </Reveal>
        <div className="relative mt-14 max-w-3xl">
          <span
            aria-hidden="true"
            className="absolute left-[5px] top-2 h-[calc(100%-16px)] w-px bg-gradient-to-b from-gold via-gold/40 to-transparent"
          />
          <ol className="space-y-12">
            {AWARDS.map((a, i) => (
              <li key={a.title} className="relative pl-10">
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-2 h-[11px] w-[11px] rotate-45 border border-gold bg-white"
                />
                <Reveal delay={i * 0.04}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gold">
                    {a.date}
                  </p>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-navy sm:text-2xl">
                    {a.title}
                  </h3>
                  {a.body && (
                    <p className="mt-2 text-[15px] leading-relaxed text-charcoal/65">
                      {a.body}
                    </p>
                  )}
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Mvv() {
  return (
    <section className="relative overflow-hidden bg-navy-deep py-20 lg:py-32" data-testid="about-mvv">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="container-x relative">
        <Reveal>
          <p className="eyebrow-navy">Our Compass</p>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-semibold leading-[1.14] text-white sm:text-4xl">
            Mission, Vision and Values
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal delay={0.05}>
            <div className="border-l-2 border-gold/60 pl-8">
              <h3 className="font-serif text-2xl font-medium italic text-gold">Our Mission</h3>
              <p className="mt-5 font-serif text-xl leading-relaxed text-white/85 sm:text-2xl">
                To provide top-tier professional consulting services that elevate
                organizational performance, build internal capacities, and foster
                economic transformation.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border-l-2 border-gold/60 pl-8">
              <h3 className="font-serif text-2xl font-medium italic text-gold">Our Vision</h3>
              <p className="mt-5 font-serif text-xl leading-relaxed text-white/85 sm:text-2xl">
                To be the most trusted and preferred partner for corporate
                strategy and business management advisory in Nigeria.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {CORE_VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="bg-navy-deep">
              <div className="h-full bg-navy-deep p-8 transition-colors duration-500 hover:bg-navy">
                <span className="font-serif text-base text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h4 className="mt-4 font-serif text-xl font-semibold text-white">
                  {v.title}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-16 text-center">
            <Link to="/contact" data-testid="about-cta" className="btn-gold">
              Schedule a Consultation
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <Seo
        title="About Us | Established 2002 | Steven Philips & Associates Limited"
        description="Established in 2002, Steven Philips & Associates Limited (SPA) is a Nigerian professional services firm providing management consultancy, accounting, audit and tax services, and enterprise software solutions including Sage 300 ERP."
      />
      <PageHero />
      <Overview />
      <TechCapabilities />
      <Industries />
      <Leadership />
      <Credentials />
      <Honours />
      <Mvv />
    </>
  );
}
